import { useId, useMemo } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Dot,
} from 'recharts'
import { ChartContainer, type ChartConfig } from '@/components/ui/chart'
import { Typography } from '@/components/ui/typography'
import { formatCurrency, formatDate } from '@/lib/format'
import type {
  ValuationHistoryPeriod,
  ValuationHistoryFund,
} from '@/services/companies.service'

interface HistoricalValuationChartProps {
  periods: ValuationHistoryPeriod[]
  isLoading?: boolean
}

const GRID_STROKE = 'rgba(184, 104, 70, 0.12)'
const AXIS_FILL = 'var(--primary)'

function buildChartConfig(funds: ValuationHistoryFund[]): ChartConfig {
  const config: ChartConfig = {}
  funds.forEach((f, i) => {
    config[f.abbreviation] = {
      label: f.abbreviation,
      color: getFundColor(i),
    }
  })
  return config
}

function formatYAxisTick(value: number): string {
  if (value === 0) return '0'
  if (Math.abs(value) >= 1_000_000_000)
    return `$${(value / 1_000_000_000).toFixed(0)}B`
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
  return `$${value}`
}

const FUND_COLORS_MULTI = [
  'var(--secondary)',
  'var(--primary)',
  'color-mix(in srgb, var(--secondary) 50%, transparent)',
  'color-mix(in srgb, var(--primary) 50%, transparent)',
]

function getFundColor(index: number): string {
  return FUND_COLORS_MULTI[index % FUND_COLORS_MULTI.length]
}

function HollowDot(props: {
  cx?: number
  cy?: number
  index?: number
  totalPoints: number
  stroke: string
}) {
  const { cx, cy, index, totalPoints, stroke } = props
  if (cx == null || cy == null) return null
  const isLast = index === totalPoints - 1
  if (isLast) {
    return <Dot cx={cx} cy={cy} r={4} fill={stroke} stroke={stroke} />
  }
  return (
    <Dot cx={cx} cy={cy} r={3} fill="white" stroke={stroke} strokeWidth={1.5} />
  )
}

function ValuationTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{
    dataKey: string
    value: number | null
    color: string
    payload: Record<string, unknown>
  }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-md border border-primary/10 bg-white px-3 py-2.5 shadow-md">
      <Typography variant="body-sm" className="mb-1.5 font-medium">
        {label}
      </Typography>
      {payload.map((entry) => {
        const sourceDate = entry.payload[`${entry.dataKey}_sourceDate`]

        return (
          <div
            key={entry.dataKey}
            className="flex items-center gap-2 py-0.5 text-xs"
          >
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-primary/70">{entry.dataKey}:</span>
            <span className="font-medium">
              {entry.value != null ? formatCurrency(entry.value) : '—'}
            </span>
            {sourceDate ? (
              <span className="text-primary/70">
                ({formatDate(sourceDate as string)})
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export function HistoricalValuationChart({
  periods,
  isLoading,
}: HistoricalValuationChartProps) {
  const gradientId = useId()
  const hasData = periods.length > 0

  const {
    chartData,
    fundAbbreviations,
    chartConfig,
    isSingleSeries,
    fundStartLabels,
  } = useMemo(() => {
    if (!hasData) {
      return {
        chartData: [],
        fundAbbreviations: [],
        chartConfig: {},
        isSingleSeries: false,
        fundStartLabels: {},
      }
    }

    const abbreviations = periods[0].funds.map((f) => f.abbreviation)
    const single = abbreviations.length <= 1

    const data = periods.map((period) => {
      const point: Record<string, unknown> = { label: period.label }
      for (const fund of period.funds) {
        point[fund.abbreviation] = fund.value
        point[`${fund.abbreviation}_sourceDate`] = fund.sourceDate
      }
      return point
    })

    const starts: Record<string, string> = {}
    for (const abbr of abbreviations) {
      const first = periods.find((p) =>
        p.funds.some((f) => f.abbreviation === abbr && f.value != null)
      )
      if (first) starts[abbr] = first.label
    }

    return {
      chartData: data,
      fundAbbreviations: abbreviations,
      chartConfig: buildChartConfig(periods[0].funds),
      isSingleSeries: single,
      fundStartLabels: starts,
    }
  }, [periods, hasData])

  if (isLoading) {
    return (
      <div className="flex h-72 items-center justify-center bg-stone-50">
        <Typography variant="body-sm" className="text-primary/70">
          Loading...
        </Typography>
      </div>
    )
  }

  if (!hasData) {
    return (
      <div className="bg-stone-50 p-4">
        <div className="flex h-72 items-center justify-center border border-dashed border-primary/20 bg-white/60">
          <Typography variant="body-sm" className="text-primary/70">
            Valuation data will be available soon
          </Typography>
        </div>
      </div>
    )
  }

  const totalPoints = chartData.length

  return (
    <div className="p-4">
      <ChartContainer config={chartConfig} className="h-72 w-full aspect-auto">
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: 12, left: -8, bottom: 8 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="rgb(184, 104, 70)"
                stopOpacity={0.15}
              />
              <stop
                offset="100%"
                stopColor="rgb(184, 104, 70)"
                stopOpacity={0.02}
              />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={GRID_STROKE} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: AXIS_FILL, fontSize: 10 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={formatYAxisTick}
            tick={{ fill: AXIS_FILL, fontSize: 10 }}
            width={52}
          />
          <Tooltip content={<ValuationTooltip />} />

          <Area
            type="monotone"
            dataKey={fundAbbreviations[0]}
            stroke={getFundColor(0)}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={(dotProps) => (
              <HollowDot
                key={dotProps.index}
                {...dotProps}
                totalPoints={totalPoints}
                stroke={getFundColor(0)}
              />
            )}
            activeDot={{ r: 4, fill: getFundColor(0) }}
            connectNulls={false}
          />
          {!isSingleSeries &&
            fundAbbreviations
              .slice(1)
              .map((abbr, i) => (
                <Line
                  key={abbr}
                  type="monotone"
                  dataKey={abbr}
                  stroke={getFundColor(i + 1)}
                  strokeWidth={1.5}
                  dot={{ r: 3, fill: getFundColor(i + 1) }}
                  connectNulls={false}
                />
              ))}
        </ComposedChart>
      </ChartContainer>
      {!isSingleSeries && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 pt-2">
          {fundAbbreviations.map((abbr, i) => (
            <div key={abbr} className="flex items-center gap-1.5 text-xs">
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: getFundColor(i) }}
              />
              <span className="font-medium">{abbr}</span>
              {fundStartLabels[abbr] && (
                <span className="text-primary/70">
                  from {fundStartLabels[abbr]}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
